<script setup lang="ts">
import {
    MtdButton,
    MtdModalMobile,
    MtdSkeleton,
    MtdSwipeableCard,
    MtdTypography,
} from '@paygine/mtd-uikit'
import { ref, useTemplateRef } from 'vue'

import { userDataApi } from '@/entities/user-data'
import { carNumberModel, VCarNumber } from '@/entities/v-car-number'
import { useTransport } from '@/features/transport'
import { AttentionIcon, GlobusIcon, TrashIcon } from '@/shared/icons'
import RussianIcon from '@/shared/icons/RussianIcon.vue'
import { formatCarNumber, transliterateCarNumber } from '@/shared/lib/formatting'

const props = defineProps<{
    transportList?: userDataApi.UserData['grn_list']
}>()

const { addTransport, deleteTransport, isLoading } = useTransport()

const externalError = ref<carNumberModel.ExternalError>({})
const currentCar = ref<null | Partial<userDataApi.Grn>>(null)
const carNumberRef = useTemplateRef<InstanceType<typeof VCarNumber>>('car-number')

async function addCarHandler() {
    const grn = currentCar.value?.grn
    const grnCountry = currentCar.value?.grn_country

    if (!grn || !grnCountry) return
    if (!(await carNumberRef.value?.validate())) return

    const result = await addTransport({ grn, grn_country: grnCountry })

    if (result?.success && result?.id) {
        isAddModalOpen.value = false
        return
    } else if (result.mask_phone) {
        externalError.value.alreadyLinkedPhone = result.mask_phone
        return
    }

    externalError.value.unknownError = true
}

async function deleteCarHandler() {
    if (!currentCar.value?.id || !props.transportList?.length) return

    await deleteTransport(currentCar.value.id, currentCar.value.grn || '')

    isDeleteModalOpen.value = false
}

function onDeleteClick(car: userDataApi.Grn) {
    isDeleteModalOpen.value = true
    currentCar.value = car
}

function updateCarNumber(payload: { country: string; grn: string }) {
    currentCar.value = {
        grn: payload.grn,
        grn_country: payload.country,
    }
    externalError.value = {}
}

const isAddModalOpen = ref(false)
const isDeleteModalOpen = ref(false)
const isAddModalReady = ref(false)

function openAddModal() {
    externalError.value = {}
    isAddModalOpen.value = true
    isAddModalReady.value = false

    //костыль из-за неправильного рассчета позиции Popup в модалке
    setTimeout(() => {
        isAddModalReady.value = true
    }, 250) // если анимация модалки 200ms, добавляем запас
}
</script>

<template>
    <div class="transport-management">
        <div class="transport-management__header">
            <MtdTypography
                variant="textbody-1-semi"
                class="transport-management__title"
                tag="h2"
                >Мой транспорт
            </MtdTypography>

            <MtdButton
                v-if="props.transportList?.length"
                variant="main-flat"
                type="button"
                class="transport-management__header-button"
                size="xs"
                @click="openAddModal"
                >Добавить</MtdButton
            >
        </div>

        <div
            v-if="props.transportList?.length"
            class="transport-list"
        >
            <MtdSwipeableCard
                v-for="transport in props.transportList"
                :key="transport.id"
                content-class="transport-list__item"
                :is-active="true"
                :auto-close="true"
            >
                <template #content>
                    <RussianIcon v-if="transport.grn_country === 'RUS'" />
                    <GlobusIcon v-else />

                    <MtdTypography
                        tag="span"
                        variant="textbody-2-medium"
                        >{{
                            transport.grn_country === 'RUS'
                                ? formatCarNumber(
                                      transliterateCarNumber(transport.grn, 'latin-to-cyrillic')
                                  )
                                : transport.grn
                        }}</MtdTypography
                    >
                </template>
                <template #button>
                    <MtdButton
                        type="button"
                        variant="danger-secondary"
                        class="transport-list__item-delete"
                        size="md"
                        @click="onDeleteClick(transport)"
                    >
                        <TrashIcon />
                    </MtdButton>
                </template>
            </MtdSwipeableCard>
        </div>

        <MtdButton
            v-if="!props.transportList?.length"
            class="transport-management__add-button"
            variant="main-secondary"
            type="button"
            @click="openAddModal"
            >Добавить</MtdButton
        >

        <MtdModalMobile
            v-model="isAddModalOpen"
            class="transport-modal"
            container-class="transport-modal__container transport-modal__container--add"
            swipe-row
            height-type="full"
            title="Добавить транспорт"
        >
            <template #content>
                <VCarNumber
                    v-if="isAddModalReady"
                    ref="car-number"
                    :enable-variants-select="true"
                    :external-error="externalError"
                    @car-number:update="updateCarNumber"
                />

                <MtdSkeleton
                    v-else
                    class="mt-24"
                    style="height: 56px; width: 100%"
                />

                <MtdButton
                    type="button"
                    variant="main"
                    class="transport-modal__add-button"
                    :is-loading="isLoading"
                    @click="addCarHandler"
                    >Добавить</MtdButton
                >
            </template>
        </MtdModalMobile>

        <MtdModalMobile
            v-model="isDeleteModalOpen"
            class="transport-modal"
            container-class="transport-modal__container transport-modal__container--delete"
        >
            <template #content>
                <AttentionIcon />

                <MtdTypography
                    variant="header-6"
                    class="default-layout__title mt-16"
                    tag="h1"
                    >Удалить
                    {{
                        currentCar?.grn_country === 'RUS'
                            ? formatCarNumber(
                                  transliterateCarNumber(currentCar?.grn || '', 'latin-to-cyrillic')
                              )
                            : currentCar?.grn
                    }}
                    ?
                </MtdTypography>

                <div class="transport-modal__button-container">
                    <MtdButton
                        class="mt-24"
                        variant="main-secondary"
                        @click="isDeleteModalOpen = false"
                        >Назад</MtdButton
                    >

                    <MtdButton
                        class="mt-24"
                        variant="danger-secondary"
                        :is-loading="isLoading"
                        @click="deleteCarHandler"
                        >Удалить</MtdButton
                    >
                </div>
            </template>
        </MtdModalMobile>
    </div>
</template>

<style src="./TransportManagement.scss" lang="scss"></style>
